import React from 'react';

// Renders JSON-LD as a real inline <script> element in the React tree (not
// injected via useEffect/document.createElement) so Prerender and non-JS
// crawlers (Bing, AI agents invited via llms.txt) see the structured data in
// the initial HTML. The <, >, & escapes keep user/entity-derived strings from
// breaking out of the ld+json block.
export default function StructuredData({ data }) {
  if (!data) return null;
  const json = JSON.stringify(data, null, 0)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />
  );
}

// Enhanced Organization & Local Business Schema
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": ["Dentist", "MedicalBusiness", "LocalBusiness", "HealthAndBeautyBusiness"],
  "name": "Trinity Dental Arts",
  "legalName": "Trinity Dental Arts PLLC",
  "image": "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/710fbb969_logo_ver1_bg_white.png",
  "logo": "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/710fbb969_logo_ver1_bg_white.png",
  "description": "Doctor-owned implant and cosmetic dental practice led by Dr. Xhoana Gjelaj at 11105 Trinity Blvd, Trinity, FL.",
  "@id": "https://trinitydentalarts.com/#organization",
  "url": "https://trinitydentalarts.com",
  "telephone": "+1-727-228-6846",
  "email": "info@trinitydentalarts.com",
  "priceRange": "$$$",
  "currenciesAccepted": "USD",
  "paymentAccepted": "Cash, Credit Card",
  "hasMap": "https://maps.app.goo.gl/dnWeZn2SyEMD1BQ48",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "11105 Trinity Blvd",
    "addressLocality": "Trinity",
    "addressRegion": "FL",
    "postalCode": "34655",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 28.186676,
    "longitude": -82.631099
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
      "opens": "09:00",
      "closes": "17:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "00:00"
    }
  ],
  "sameAs": [
    "https://www.facebook.com/trinitydentalarts",
    "https://www.instagram.com/trinitydentalarts",
    "https://www.youtube.com/@trinitydentalarts",
    "https://maps.app.goo.gl/dnWeZn2SyEMD1BQ48",
    "https://www.google.com/maps?cid=6368379649761431467",
    "https://www.yelp.com/biz/trinity-dental-arts-trinity",
    "https://www.healthgrades.com/dentist/dr-xhoana-gjelaj-xbk4c",
    "https://www.zocdoc.com/practice/trinity-dental-arts-101492",
    "https://www.vitals.com/dentists/Dr_Xhoana_Gjelaj.html",
    "https://www.ratemds.com/dentist/trinity-dental-arts",
    "https://www.bbb.org/us/fl/trinity/profile/dentist/trinity-dental-arts",
    "https://www.dentistrytoday.com",
    "https://local.google.com/place?id=trinity-dental-arts",
    "https://www.ada.org",
    "https://agd.org",
    "https://aaid-implant.org",
    "https://icoi.world",
    "https://www.aboi.org"
  ],
  "foundingDate": "2009",
  "foundingLocation": {
    "@type": "Place",
    "name": "Trinity, Florida"
  },
  "numberOfEmployees": {
    "@type": "QuantitativeValue",
    "value": 10
  },
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+1-727-228-6846",
      "contactType": "customer service",
      "areaServed": ["FL", "US"],
      "availableLanguage": ["English"],
      "hoursAvailable": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday"],
        "opens": "09:00",
        "closes": "17:00"
      }
    },
    {
      "@type": "ContactPoint",
      "email": "info@trinitydentalarts.com",
      "contactType": "customer support"
    }
  ],
  "areaServed": [

    {"@type": "City", "name": "Trinity", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "New Port Richey", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "Tampa", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "Clearwater", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "St. Petersburg", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "Palm Harbor", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "Tarpon Springs", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "Holiday", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "Odessa", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "City", "name": "Lutz", "containedInPlace": {"@type": "AdministrativeArea", "name": "Florida"}},
    {"@type": "Region", "name": "Pasco County"},
    {"@type": "Region", "name": "Hillsborough County"},
    {"@type": "Region", "name": "Pinellas County"},
    {"@type": "Region", "name": "Tampa Bay Area"}
  ],
  "medicalSpecialty": [
    "Dentistry",
    "Implant Dentistry",
    "Cosmetic Dentistry",
    "Oral Surgery",
    "Prosthodontics"
  ],
  "knowsAbout": [
    "Dental Implants",
    "All-on-4 Implants",
    "All-on-6 Implants",
    "Single Tooth Implants",
    "Implant-Supported Dentures",
    "Cosmetic Dentistry",
    "Teeth Whitening",
    "Porcelain Veneers",
    "Smile Makeovers",
    "Full Mouth Reconstruction",
    "Bone Grafting",
    "Sinus Lifts",
    "Periodontal Treatment",
    "Laser Dentistry",
    "Sedation Dentistry",
    "Emergency Dentistry",
    "General Dentistry",
    "Preventative Dentistry",
    "Invisalign Clear Aligners",
    "Same-Day Crowns",
    "Regenerative Dentistry"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Dental Services",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Dental Implants", "description": "State-of-the-art tooth replacement using surgical implants. Includes single implants, All-on-4®, All-on-6®, and full mouth restoration.", "procedureType": "Surgical", "bodyLocation": "Mouth", "provider": { "@type": "Dentist", "name": "Trinity Dental Arts" } } } },
      { "@type": "ListItem", "position": 2, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Cosmetic Dentistry", "description": "Smile transformation services including porcelain veneers, professional whitening, smile makeovers, and digital smile design.", "procedureType": "Cosmetic", "bodyLocation": "Teeth", "provider": { "@type": "Dentist", "name": "Trinity Dental Arts" } } } },
      { "@type": "ListItem", "position": 3, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "General Dentistry", "description": "Comprehensive preventive and restorative dental care including cleanings, exams, fillings, crowns, and bridges.", "procedureType": "Preventive", "bodyLocation": "Mouth", "provider": { "@type": "Dentist", "name": "Trinity Dental Arts" } } } },
      { "@type": "ListItem", "position": 4, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Oral Surgery", "description": "Advanced surgical procedures including tooth extraction, wisdom teeth removal, bone grafting, and implant placement.", "procedureType": "Surgical", "bodyLocation": "Mouth/Jaw", "provider": { "@type": "Dentist", "name": "Trinity Dental Arts" } } } },
      { "@type": "ListItem", "position": 5, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Periodontal Treatment", "description": "Advanced treatment for gum disease and periodontal conditions to save teeth and restore oral health.", "procedureType": "Therapeutic", "bodyLocation": "Gum", "provider": { "@type": "Dentist", "name": "Trinity Dental Arts" } } } },
      { "@type": "ListItem", "position": 6, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Laser Dentistry", "description": "Precise, minimally invasive laser-assisted procedures with reduced bleeding and faster healing times.", "procedureType": "Therapeutic", "bodyLocation": "Mouth", "provider": { "@type": "Dentist", "name": "Trinity Dental Arts" } } } },
      { "@type": "ListItem", "position": 7, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Sedation Dentistry", "description": "Safe sedation options for anxious patients, from nitrous oxide to IV sedation for comfortable dental care.", "procedureType": "Therapeutic", "bodyLocation": "Mouth", "provider": { "@type": "Dentist", "name": "Trinity Dental Arts" } } } },
      { "@type": "ListItem", "position": 8, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Emergency Dentistry", "description": "Immediate care for dental emergencies including broken teeth, severe pain, infections, and traumatic injuries.", "procedureType": "Emergency", "bodyLocation": "Mouth", "provider": { "@type": "Dentist", "name": "Trinity Dental Arts" } } } }
    ]
  }
};

// Person Schema for Dr. Gjelaj (Enhanced with Physician/Dentist credentials)
export const doctorSchema = {
   "@context": "https://schema.org",
   "@type": ["Dentist", "Physician", "Person"],
   "@id": "https://trinitydentalarts.com/meet-the-doctor",
   "name": "Dr. Xhoana Gjelaj",
   "honorificPrefix": "Dr.",
   "givenName": "Xhoana",
   "familyName": "Gjelaj",
   "jobTitle": "Board-Certified Implantologist & Cosmetic Dentist",
   "description": "Dr. Xhoana Gjelaj holds ABOI/ID board certification in implant dentistry, AGD Mastership, ICOI Diplomate status and AAID Fellowship. She plans implant and restorative treatment around each patient’s health, bite and goals.",
   "image": "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/fb403896a_IMG_0190.jpg",
   "alumniOf": [
     {
       "@type": "EducationalOrganization",
       "name": "Nova Southeastern University",
       "sameAs": "https://www.nova.edu/"
     },
     {
       "@type": "EducationalOrganization",
       "name": "Dawson Academy"
     },
     {
       "@type": "EducationalOrganization",
       "name": "Pankey Institute"
     }
   ],
   "hasCredential": [
     {
       "@type": "EducationalOccupationalCredential",
       "credentialCategory": "degree",
       "name": "Doctor of Dental Medicine (DMD)",
       "educationalLevel": "Doctorate"
     },
     {
       "@type": "EducationalOccupationalCredential",
       "credentialCategory": "certification",
       "name": "Master of the Academy of General Dentistry (MAGD)",
       "recognizedBy": {
         "@type": "Organization",
         "name": "Academy of General Dentistry",
         "sameAs": "https://agd.org"
       }
     },
     {
       "@type": "EducationalOccupationalCredential",
       "credentialCategory": "certification",
       "name": "Diplomate of American Board of Oral Implantology/Implant Dentistry (DABOI/ID)",
       "recognizedBy": {
         "@type": "Organization",
         "name": "American Board of Oral Implantology",
         "sameAs": "https://www.aboi.org"
       }
     },
     {
       "@type": "EducationalOccupationalCredential",
       "credentialCategory": "certification",
       "name": "Diplomate of International Congress of Oral Implantology (DICOI)",
       "recognizedBy": {
         "@type": "Organization",
         "name": "International Congress of Oral Implantologists",
         "sameAs": "https://icoi.world"
       }
     },
     {
       "@type": "EducationalOccupationalCredential",
       "credentialCategory": "certification",
       "name": "Fellow of American Academy of Implant Dentistry (FAAID)",
       "recognizedBy": {
         "@type": "Organization",
         "name": "American Academy of Implant Dentistry",
         "sameAs": "https://aaid-implant.org"
       }
     }
   ],
   "memberOf": [
     {
       "@type": "Organization",
       "name": "American Academy of Implant Dentistry"
     },
     {
       "@type": "Organization",
       "name": "American Board of Oral Implantology"
     },
     {
       "@type": "Organization",
       "name": "Academy of General Dentistry"
     },
     {
       "@type": "Organization",
       "name": "International Congress of Oral Implantologists"
     }
   ],
   "url": "https://trinitydentalarts.com/meet-the-doctor",
   "sameAs": [
     "https://www.healthgrades.com/dentist/dr-xhoana-gjelaj-xbk4c",
     "https://www.vitals.com/dentists/Dr_Xhoana_Gjelaj.html",
     "https://www.zocdoc.com/practice/trinity-dental-arts-101492",
     "https://aaid-implant.org",
     "https://www.aboi.org",
     "https://icoi.world",
     "https://agd.org",
     "https://www.ada.org"
   ],
   "telephone": "+1-727-228-6846",
   "email": "info@trinitydentalarts.com",
   "address": {
     "@type": "PostalAddress",
     "streetAddress": "11105 Trinity Blvd",
     "addressLocality": "Trinity",
     "addressRegion": "FL",
     "postalCode": "34655",
     "addressCountry": "US"
   },
   "areaServed": [
     {"@type": "City", "name": "Trinity", "containedInPlace": {"@type": "State", "name": "Florida"}},
     {"@type": "City", "name": "Tampa", "containedInPlace": {"@type": "State", "name": "Florida"}},
     {"@type": "City", "name": "Clearwater", "containedInPlace": {"@type": "State", "name": "Florida"}},
     {"@type": "City", "name": "St. Petersburg", "containedInPlace": {"@type": "State", "name": "Florida"}},
     {"@type": "AdministrativeArea", "name": "Pasco County"},
     {"@type": "AdministrativeArea", "name": "Hillsborough County"},
     {"@type": "AdministrativeArea", "name": "Pinellas County"},
     {"@type": "Place", "name": "Tampa Bay Area"}
   ],
   "award": [
     "Master of Academy of General Dentistry (MAGD) — 1,100+ hours of advanced continuing education",
     "Diplomate of American Board of Oral Implantology (DABOI/ID)",
     "Diplomate of International Congress of Oral Implantology (DICOI)",
     "Fellow of American Academy of Implant Dentistry (FAAID)"
   ],
   "knowsAbout": [
     "Dental Implants",
     "All-on-4 Dental Implants",
     "All-on-6 Dental Implants",
     "Single Tooth Implants",
     "Implant-Supported Dentures",
     "Bone Grafting",
     "Sinus Lift Surgery",
     "Regenerative Dentistry",
     "Cosmetic Dentistry",
     "Porcelain Veneers",
     "Smile Makeovers",
     "Digital Smile Design",
     "Full Mouth Reconstruction",
     "Periodontal Treatment",
     "Laser Dentistry",
     "Sedation Dentistry",
     "Emergency Dentistry",
     "General Dentistry",
     "Preventative Dentistry",
     "Invisalign Clear Aligners",
     "Same-Day Crowns",
     "Teeth Whitening",
     "Oral Surgery",
     "CEREC Technology",
     "3D Cone Beam Imaging"
   ],
   "specialty": [
     "Implant Dentistry",
     "Oral Surgery",
     "Cosmetic Dentistry",
     "Prosthodontics",
     "Full Mouth Reconstruction"
   ],
   "medicalSpecialty": [
     {
       "@type": "MedicalSpecialty",
       "name": "Dentistry"
     },
     {
       "@type": "MedicalSpecialty",
       "name": "Implant Dentistry"
     },
     {
       "@type": "MedicalSpecialty",
       "name": "Oral Surgery"
     },
     {
       "@type": "MedicalSpecialty",
       "name": "Cosmetic Dentistry"
     }
   ],
   "performsService": [
     {
       "@type": "MedicalProcedure",
       "name": "Dental Implant Surgery"
     },
     {
       "@type": "MedicalProcedure",
       "name": "All-on-4 Full Arch Restoration"
     },
     {
       "@type": "MedicalProcedure",
       "name": "Bone Grafting and Sinus Augmentation"
     },
     {
       "@type": "MedicalProcedure",
       "name": "Cosmetic Smile Makeovers"
     }
   ],
   "worksFor": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts",
     "url": "https://trinitydentalarts.com"
   },
   "yearsOfExperience": 15
};

// Service Schema Generator (Enhanced for Google AI)
export const createServiceSchema = (serviceName, description, price) => ({
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  "name": serviceName,
  "description": description,
  "image": "https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/697e88817b05b4051a928e3f/710fbb969_logo_ver1_bg_white.png",
  "url": "https://trinitydentalarts.com",
  "telephone": "+1-727-228-6846",
  "areaServed": [
    {"@type": "City", "name": "Trinity"},
    {"@type": "City", "name": "Tampa"},
    {"@type": "City", "name": "Clearwater"},
    {"@type": "Region", "name": "Tampa Bay Area"}
  ],
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "USD",
    "lowPrice": price,
    "availability": "https://schema.org/InStock",
    "offerCount": "1"
  },
  "provider": {
    "@type": "LocalBusiness",
    "name": "Trinity Dental Arts",
    "url": "https://trinitydentalarts.com",
    "telephone": "+1-727-228-6846"
  },
  "medicalSpecialty": "Dentistry"
});

// FAQ Schema Generator
export const createFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

// Review Schema
export const reviewSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Trinity Dental Arts"
};

// MedicalBusiness Schema
export const medicalBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  "name": "Trinity Dental Arts",
  "image": "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=1200&q=80",
  "description": "Premier dental practice specializing in dental implants, cosmetic dentistry, and general dentistry in Trinity, FL. Board-certified implantologist Dr. Xhoana Gjelaj provides expert care.",
  "@id": "https://trinitydentalarts.com",
  "url": "https://trinitydentalarts.com",
  "telephone": "+1-727-228-6846",
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "11105 Trinity Blvd",
    "addressLocality": "Trinity",
    "addressRegion": "FL",
    "postalCode": "34655",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 28.1836,
    "longitude": -82.6470
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"],
      "opens": "09:00",
      "closes": "17:00"
    }
  ],
  "medicalSpecialty": [
    "Dentistry",
    "Oral Surgery",
    "Cosmetic Dentistry",
    "Prosthodontics"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Dental Services",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Dental Implants", "description": "Permanent tooth replacement solutions", "provider": {"@type": "Dentist", "name": "Trinity Dental Arts"} } } },
      { "@type": "ListItem", "position": 2, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Cosmetic Dentistry", "description": "Smile transformation and enhancement with veneers and whitening", "provider": {"@type": "Dentist", "name": "Trinity Dental Arts"} } } },
      { "@type": "ListItem", "position": 3, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "General Dentistry", "description": "Comprehensive preventive and restorative oral health care" } } },
      { "@type": "ListItem", "position": 4, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Oral Surgery & Extractions", "description": "Advanced surgical procedures and tooth extraction" } } },
      { "@type": "ListItem", "position": 5, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Periodontal Treatment", "description": "Gum disease and periodontal condition treatment" } } },
      { "@type": "ListItem", "position": 6, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Sedation Dentistry", "description": "Comfortable dental care for anxious patients" } } },
      { "@type": "ListItem", "position": 7, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Laser Dentistry", "description": "Precise, minimally invasive laser-assisted procedures" } } },
      { "@type": "ListItem", "position": 8, "item": { "@type": "Offer", "itemOffered": { "@type": "MedicalProcedure", "name": "Emergency Dentistry", "description": "Immediate care for dental emergencies" } } }
    ]
  }
};

// Enhanced Dental Implants Service Schema
export const dentalImplantsServiceSchema = {
  "@context": "https://schema.org",
  "@type": ["MedicalProcedure", "Service"],
  "name": "Dental Implants - Trinity, FL",
  "alternateName": ["Teeth Implants", "All-on-4 Implants", "All-on-6 Implants", "Single Tooth Implants"],
  "description": "State-of-the-art dental implants that look, feel, and function like natural teeth. From single tooth replacement to full mouth restoration with All-on-4® and All-on-6® technology. Board-certified implantologist Dr. Gjelaj with 20+ years experience and over 95% success rate.",
  "procedureType": "Surgical",
  "bodyLocation": {
    "@type": "BodySystem",
    "name": "Mouth"
  },
  "followup": "Regular dental check-ups every 6 months and professional cleanings",
  "howPerformed": "Computer-guided surgical implant placement using advanced 3D cone beam CT imaging for precision. Titanium posts surgically placed in jawbone to act as tooth roots, followed by crown placement after osseointegration.",
  "preparation": "Comprehensive consultation, 3D imaging, health assessment, and custom treatment planning",
  "medicationClass": "Local anesthesia, sedation options available (nitrous oxide, oral sedation, IV sedation)",
  "typicalAgeRange": "18-80",
  "status": "Accepted medical practice",
  "availableService": [
    {
      "@type": "MedicalTherapy",
      "name": "Single Tooth Implant",
      "description": "Individual tooth replacement with dental implant and crown"
    },
    {
      "@type": "MedicalTherapy",
      "name": "All-on-4 Full Arch Restoration",
      "description": "Complete arch of teeth supported by 4 strategically placed implants"
    },
    {
      "@type": "MedicalTherapy",
      "name": "All-on-6 Full Arch Restoration",
      "description": "Complete arch of teeth supported by 6 implants for maximum stability"
    },
    {
      "@type": "MedicalTherapy",
      "name": "Implant-Supported Dentures",
      "description": "Dentures secured by dental implants for superior fit and function"
    },
    {
      "@type": "MedicalTherapy",
      "name": "Same-Day Implants",
      "description": "Immediate implant placement and temporary teeth in one visit"
    }
  ],
  "procedureDuration": "PT1H to PT4H",
  "recoveryTime": "PT3M to PT6M",
  "benefits": [
    "Permanent tooth replacement solution lasting 25+ years",
    "Prevents bone loss and preserves facial structure",
    "Natural look, feel, and function",
    "No impact on adjacent teeth",
    "95%+ success rate",
    "Improved chewing ability and speech"
  ],
  "suitableFor": [
    "Patients with missing teeth",
    "Failed root canal or broken tooth requiring extraction",
    "Denture wearers seeking permanent solution",
    "Those with adequate bone density or willing to undergo bone grafting"
  ],
  "provider": {
    "@type": "Dentist",
    "name": "Dr. Xhoana Gjelaj",
    "hasCredential": "MAGD, DABOI/ID, DICOI, FAAID"
  },
  "serviceLocation": {
    "@type": "MedicalBusiness",
    "name": "Trinity Dental Arts",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "11105 Trinity Blvd",
      "addressLocality": "Trinity",
      "addressRegion": "FL",
      "postalCode": "34655"
    },
    "telephone": "+1-727-228-6846"
  },
  "offers": {
    "@type": "Offer",
    "priceCurrency": "USD",
    "priceRange": "$$$",
    "availability": "https://schema.org/InStock",
    "availableDeliveryMethod": "https://schema.org/OnSitePickup"
  }
};

// Enhanced Cosmetic Dentistry Service Schema
export const cosmeticDentistryServiceSchema = {
  "@context": "https://schema.org",
  "@type": ["MedicalProcedure", "Service"],
  "name": "Cosmetic Dentistry - Trinity, FL",
  "alternateName": ["Smile Makeover", "Teeth Whitening", "Porcelain Veneers", "Smile Design"],
  "description": "Transform your smile with advanced cosmetic dentistry including porcelain veneers, professional teeth whitening, complete smile makeovers, and digital smile design technology. Achieve the confident, beautiful smile you've always wanted with board-certified cosmetic dentist Dr. Gjelaj.",
  "procedureType": "Cosmetic",
  "bodyLocation": {
    "@type": "BodySystem",
    "name": "Teeth"
  },
  "followup": "Annual touch-ups and regular dental maintenance",
  "howPerformed": "Customized treatment using digital smile design, high-quality materials, and precision craftsmanship for natural-looking results",
  "preparation": "Smile consultation, digital imaging, shade matching, and personalized treatment planning",
  "typicalAgeRange": "18-80",
  "status": "Accepted medical practice",
  "availableService": [
    {
      "@type": "MedicalTherapy",
      "name": "Porcelain Veneers",
      "description": "Ultra-thin ceramic shells bonded to front teeth for perfect shape, size, and color"
    },
    {
      "@type": "MedicalTherapy",
      "name": "Professional Teeth Whitening",
      "description": "Safe, effective whitening for teeth up to 8 shades brighter"
    },
    {
      "@type": "MedicalTherapy",
      "name": "Complete Smile Makeover",
      "description": "Comprehensive transformation combining multiple cosmetic procedures"
    },
    {
      "@type": "MedicalTherapy",
      "name": "Digital Smile Design",
      "description": "Preview your new smile before treatment with advanced 3D technology"
    },
    {
      "@type": "MedicalTherapy",
      "name": "Composite Bonding",
      "description": "Quick repair of chips, gaps, and discoloration with tooth-colored resin"
    },
    {
      "@type": "MedicalTherapy",
      "name": "Gum Contouring",
      "description": "Laser reshaping of gum line for balanced, symmetrical smile"
    }
  ],
  "procedureDuration": "PT1H to PT4H",
  "recoveryTime": "PT0H to PT3D",
  "benefits": [
    "Dramatically improved smile aesthetics",
    "Boosted self-confidence and self-esteem",
    "Natural-looking, long-lasting results",
    "Corrects chips, gaps, stains, and misalignment",
    "Minimally invasive procedures",
    "Custom-designed for your facial features"
  ],
  "suitableFor": [
    "Patients seeking smile enhancement",
    "Stained or discolored teeth",
    "Chipped or cracked teeth",
    "Gaps between teeth",
    "Misshapen or uneven teeth",
    "Special events (weddings, interviews, etc.)"
  ],
  "provider": {
    "@type": "Dentist",
    "name": "Dr. Xhoana Gjelaj",
    "hasCredential": "MAGD, DICOI, Cosmetic Dentistry Expert"
  },
  "serviceLocation": {
    "@type": "MedicalBusiness",
    "name": "Trinity Dental Arts",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "11105 Trinity Blvd",
      "addressLocality": "Trinity",
      "addressRegion": "FL",
      "postalCode": "34655"
    },
    "telephone": "+1-727-228-6846"
  },
  "offers": {
    "@type": "Offer",
    "priceCurrency": "USD",
    "priceRange": "$$-$$$",
    "availability": "https://schema.org/InStock"
  }
};

export const generalDentistryServiceSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "General Dentistry",
   "description": "Comprehensive preventive and restorative dental care including cleanings, exams, crowns, bridges, root canals, and periodontal treatment.",
   "procedureType": "Preventive",
   "bodyLocation": "Mouth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Full mouth restoration schema
export const fullMouthRestorationSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Full Mouth Restoration",
   "description": "Complete smile transformation with multiple dental implants and prosthetics. Restore function and aesthetics with All-on-4® or All-on-6® solutions.",
   "procedureType": "Surgical",
   "bodyLocation": "Mouth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Invisalign schema
export const invisalignServiceSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Invisalign Clear Aligners",
   "description": "Discreet orthodontic treatment with custom-made clear aligners. Straighten teeth without braces.",
   "procedureType": "Orthdontic",
   "bodyLocation": "Mouth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Emergency dentistry schema
export const emergencyDentistrySchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Emergency Dentistry",
   "description": "Immediate care for dental emergencies including broken teeth, severe pain, infections, and traumatic injuries.",
   "procedureType": "Emergency",
   "bodyLocation": "Mouth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Periodontal disease treatment schema
export const periodontalTreatmentSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Periodontal Disease Treatment",
   "description": "Advanced treatment for gum disease and periodontal conditions to save teeth and restore oral health.",
   "procedureType": "Therapeutic",
   "bodyLocation": "Gum",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Bone grafting schema
export const boneGraftingSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Bone Grafting",
   "description": "Surgical bone augmentation to restore jaw bone for successful dental implant placement.",
   "procedureType": "Surgical",
   "bodyLocation": "Jaw",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Sinus lift schema
export const sinusLiftSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Sinus Lift (Sinus Augmentation)",
   "description": "Advanced surgical procedure to create adequate bone height in the upper jaw for dental implant placement.",
   "procedureType": "Surgical",
   "bodyLocation": "Jaw",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Oral surgery schema
export const oralSurgerySchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Oral Surgery",
   "description": "Advanced surgical procedures including tooth extraction, wisdom teeth removal, and complex implant placement.",
   "procedureType": "Surgical",
   "bodyLocation": "Mouth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Professional teeth whitening schema
export const teethWhiteningSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Professional Teeth Whitening",
   "description": "Professional-grade teeth whitening treatment for a brighter, more radiant smile. Safe, effective, and fast results.",
   "procedureType": "Cosmetic",
   "bodyLocation": "Teeth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Same-day crowns schema
export const sameDayCrownsSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Same-Day Dental Crowns",
   "description": "Using advanced CAD/CAM technology, we create and place custom ceramic crowns in a single visit without temporary crowns.",
   "procedureType": "Restorative",
   "bodyLocation": "Tooth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Laser dentistry schema
export const laserDentistrySchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Laser Dentistry",
   "description": "Advanced laser technology for precise, minimally invasive dental treatments with reduced bleeding and faster healing.",
   "procedureType": "Therapeutic",
   "bodyLocation": "Mouth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Sedation dentistry schema
export const sedationDentistrySchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Sedation Dentistry",
   "description": "Safe sedation options for anxious patients, from nitrous oxide to IV sedation, ensuring comfortable dental care.",
   "procedureType": "Therapeutic",
   "bodyLocation": "Mouth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Preventative dentistry schema
export const preventativeDentistrySchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Preventative Dentistry",
   "description": "Comprehensive preventive care including cleanings, exams, fluoride treatments, and sealants to maintain optimal oral health.",
   "procedureType": "Preventive",
   "bodyLocation": "Mouth",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// Regenerative dentistry schema
export const regenerativeDentistrySchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Regenerative Dentistry",
   "description": "Advanced biological techniques to regenerate bone and periodontal tissues, preserving natural teeth and supporting implant success.",
   "procedureType": "Surgical",
   "bodyLocation": "Jaw/Gum",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};

// All-on-4 / Fixed new teeth schema
export const fixedNewTeethSchema = {
   "@context": "https://schema.org",
   "@type": "MedicalProcedure",
   "name": "Fixed New Teeth (All-on-4®/All-on-6®)",
   "description": "Complete mouth restoration using 4-6 strategically placed implants to support a full set of permanent teeth.",
   "procedureType": "Surgical",
   "bodyLocation": "Mouth/Jaw",
   "provider": {
     "@type": "Dentist",
     "name": "Trinity Dental Arts"
   }
};